import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import axios from '../../../helpers/axios';
import { Modal, Upload, message, Avatar, Descriptions, Button, Tag, Spin } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { handleAxiosError } from '../../../helpers/errorHandler';

const { Dragger } = Upload;

const Profile = ({ open, onClose, userData }) => {
  const [signature, setSignature] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingSignature, setLoadingSignature] = useState(false);

  const fetchSignature = async () => {
    if (!userData?.with_signature) return;

    const token = Cookies.get('access_token');
    if (!token) return;

    try {
      setLoadingSignature(true);
      const res = await axios.get('/api/signserver/signature/', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.signature_image) {
        const imageUrl = `data:image/png;base64,${res.data.signature_image}`;
        setSignature(imageUrl);
      }
    } catch (err) {
      handleAxiosError(err, 'Failed to fetch signature.');
      console.error('Failed to fetch signature:', err);
    } finally {
      setLoadingSignature(false);
    }
  };

  useEffect(() => {
    if (open) fetchSignature();
  }, [open, userData]);

  const handleUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Only image files are allowed for signatures.');
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = (e) => setSignature(e.target.result);
    reader.readAsDataURL(file);

    return false; // prevent auto upload
  };

  const handleUploadSignature = async () => {
    if (!signature) {
      return message.warning('Please upload a signature.');
    }

    const token = Cookies.get('access_token');
    if (!token) return message.error('No access token found.');

    try {
      setUploading(true);
      message.loading({ content: 'Uploading signature...', key: 'upload' });

      const base64Data = signature.split(',')[1];
      const binary = atob(base64Data);
      const array = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
      const signatureBlob = new Blob([array], { type: 'image/png' });

      const formData = new FormData();
      formData.append('signature_image', signatureBlob, 'signature.png');

      await axios.post('/api/signserver/signature/', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success({
        content: 'Signature uploaded successfully!',
        key: 'upload',
        duration: 2,
      });

      await fetchSignature();

      setTimeout(() => {
        onClose();
      }, 600);
    } catch (err) {
      message.error({
        content: 'Failed to upload signature. Please try again.',
        key: 'upload',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center gap-3">
          <Avatar size={48} src={require('../../../static/img/avatar/profileImage.png')} />
          <div>
            <h3 className="text-base font-semibold mb-0">{userData?.username || 'User Profile'}</h3>
            <p className="text-xs text-gray-500">{userData?.email}</p>
          </div>
        </div>
      }
      footer={null}
      width={700}
      centered
    >
      <div className="p-4">
        <Descriptions bordered column={1} size="small" labelStyle={{ fontWeight: 500, width: 150 }}>
          <Descriptions.Item label="Username">{userData?.username || '—'}</Descriptions.Item>
          <Descriptions.Item label="Email">{userData?.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="Role">{userData?.role || '—'}</Descriptions.Item>
          <Descriptions.Item label="Department">{userData?.department || '—'}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={userData?.status_id === 1 ? 'green' : 'red'}>
              {userData?.status_id === 1 ? 'Active' : 'Inactive'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Date Joined">
            {userData?.date_joined ? moment(userData.date_joined).format('MMMM DD, YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Last Login">
            {userData?.last_login ? moment(userData.last_login).format('MMMM DD, YYYY') : '—'}
          </Descriptions.Item>
        </Descriptions>

        <div className="mt-6">
          <h4 className="text-sm text-center font-semibold mb-2">{signature ? 'Signature' : 'Upload Signature'}</h4>

          {loadingSignature ? (
            <div className="flex justify-center py-6">
              <Spin tip="Loading signature..." />
            </div>
          ) : signature ? (
            <div className="flex flex-col items-center">
              <img src={signature} alt="Signature" className="max-h-32 object-contain border p-2 rounded-md" />
            </div>
          ) : (
            <Dragger
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleUpload}
              className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border-dashed border-gray-300 hover:border-primary"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag your signature image here to upload</p>
              <p className="ant-upload-hint text-xs text-gray-500">
                Supported formats: PNG, JPG. Recommended transparent PNG.
              </p>
            </Dragger>
          )}

          {!userData?.with_signature && signature && !loadingSignature && (
            <div className="mt-4 flex justify-center">
              <Button
                type="primary"
                loading={uploading}
                className="bg-primary hover:bg-primary/90"
                onClick={handleUploadSignature}
              >
                Save Signature
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default Profile;
